import { relations } from 'drizzle-orm';
import { integer, numeric, pgEnum, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core';
import * as z from 'zod';
import { orders } from './production-orders.js';
import { patientTherapySequence } from './patient-therapy.js';
import { timestamps } from './utils/index.js';
import { resourceStatus } from './utils/resource-status.js';
import { userActions } from './utils/user-actions.js';
import { uuidv7 } from './utils/uuidv7.js';
export const CompoundOrderStatus = z.enum(['PENDING', 'SENT', 'RESULT_RECEIVED', 'FAILED', 'VOID']);
export const compoundOrderStatusEnum = pgEnum('compound_order_status', [
    CompoundOrderStatus.enum.PENDING,
    CompoundOrderStatus.enum.SENT,
    CompoundOrderStatus.enum.RESULT_RECEIVED,
    CompoundOrderStatus.enum.FAILED,
    CompoundOrderStatus.enum.VOID,
]);
export const CompoundMappingType = z.enum(['DRUG', 'CARRIER', 'PATIENT_CONTAINER', 'IV_SET']);
export const compoundMappingTypeEnum = pgEnum('compound_mapping_type', [
    CompoundMappingType.enum.DRUG,
    CompoundMappingType.enum.CARRIER,
    CompoundMappingType.enum.PATIENT_CONTAINER,
    CompoundMappingType.enum.IV_SET,
]);
export const compoundOrders = pgTable('compound_orders', {
    id: uuidv7('id').primaryKey(),
    // Unique identifier of the preparation in the external (SMC) system.
    externalId: text('external_id').notNull(),
    therapySequenceId: uuid('therapy_sequence_id')
        .notNull()
        .references(() => patientTherapySequence.id, { onDelete: 'cascade' }),
    orderId: uuid('order_id')
        .notNull()
        .references(() => orders.id, { onDelete: 'restrict' }),
    status: compoundOrderStatusEnum('status').notNull().default(CompoundOrderStatus.enum.PENDING),
    // The generated SMC <Preparation> XML, stored for audit and regeneration.
    machineOrderXml: text('machine_order_xml'),
    generatedAt: timestamp('generated_at'),
    sentAt: timestamp('sent_at'),
    error: text('error'),
    ...timestamps(),
    ...userActions(),
});
export const compoundOrderRelations = relations(compoundOrders, ({ one }) => ({
    therapySequence: one(patientTherapySequence, {
        fields: [compoundOrders.therapySequenceId],
        references: [patientTherapySequence.id],
    }),
    order: one(orders, {
        fields: [compoundOrders.orderId],
        references: [orders.id],
    }),
    result: one(compoundResults, {
        fields: [compoundOrders.id],
        references: [compoundResults.compoundOrderId],
    }),
}));
export const compoundResults = pgTable('compound_results', {
    id: uuidv7('id').primaryKey(),
    compoundOrderId: uuid('compound_order_id')
        .notNull()
        .unique()
        .references(() => compoundOrders.id, { onDelete: 'cascade' }),
    // Text, not timestamp: the machine reports the preparation date/time as a free-form ISO 8601
    // string (e.g. "2021-02-18T15:02:25+01:00", sometimes without a timezone offset). Stored
    // verbatim to preserve fidelity and avoid lossy/normalizing parses.
    preparationDate: text('preparation_date'),
    // Release status as reported by the machine:
    //   0 = Unknown, 1 = Invalid, 2 = Released, 3 = Invalid and copied
    releaseStatus: integer('release_status').notNull().default(0),
    releaseComment: text('release_comment'),
    measuredCarrierVolume: numeric('measured_carrier_volume', { mode: 'number' }),
    measuredDrugDose: numeric('measured_drug_dose', { mode: 'number' }),
    carrierDeviation: numeric('carrier_deviation', { mode: 'number' }),
    drugDeviation: numeric('drug_deviation', { mode: 'number' }),
    rawXml: text('raw_xml').notNull(),
    receivedAt: timestamp('received_at').notNull().defaultNow(),
    ...timestamps(),
    ...userActions(),
});
export const compoundResultRelations = relations(compoundResults, ({ one, many }) => ({
    compoundOrder: one(compoundOrders, {
        fields: [compoundResults.compoundOrderId],
        references: [compoundOrders.id],
    }),
    components: many(compoundResultComponents),
}));
export const compoundResultComponents = pgTable('compound_result_components', {
    id: uuidv7('id').primaryKey(),
    compoundResultId: uuid('compound_result_id')
        .notNull()
        .references(() => compoundResults.id, { onDelete: 'cascade' }),
    componentTypeId: text('component_type_id'),
    internalId: text('internal_id'),
    lotNumber: text('lot_number'),
    expirationDate: text('expiration_date'),
    stockLocation: text('stock_location'),
    specifiedVolume: numeric('specified_volume', { mode: 'number' }),
    measuredVolume: numeric('measured_volume', { mode: 'number' }),
    productFunction: text('product_function'),
    ...timestamps(),
});
export const compoundResultComponentRelations = relations(compoundResultComponents, ({ one }) => ({
    compoundResult: one(compoundResults, {
        fields: [compoundResultComponents.compoundResultId],
        references: [compoundResults.id],
    }),
}));
// Carrier type as required by the machine (0-3). See the `carrierType` column comment on compound_mappings in the DB schema definition for the value meanings.
export const CompoundCarrierType = z.int().min(0).max(3);
export const compoundMappings = pgTable('compound_mappings', {
    id: uuidv7('id').primaryKey(),
    // Discriminator for the polymorphic sourceId reference.
    mappingType: compoundMappingTypeEnum('mapping_type').notNull(),
    // References the cypro entity (drug_catalog_items, application_forms or consumables) or
    // NULL for a global default (used when no specific mapping exists for the entity).
    sourceId: uuid('source_id'),
    // The identifier as configured in the machine's Master Data.
    machineId: text('machine_id').notNull(),
    // For CARRIER mappings: 0 = no carrier, 1 = NaCl 0.9%, 2 = Glucose 5%, 3 = Glucose 10%.
    carrierType: integer('carrier_type'),
    // Human readable label, e.g. "NaCl 0.9% -> NaCl bag".
    label: text('label'),
    status: resourceStatus('status').default('active'),
    ...timestamps(),
    ...userActions(),
}, t => [unique('compound_mappings_type_source_unique').on(t.mappingType, t.sourceId)]);
//# sourceMappingURL=compounder.js.map